import React, { CSSProperties, MouseEvent, PropsWithChildren, useState } from "react";
import { isNotNull } from "utils/types/typeguards";
import { Link } from "react-router-dom";
import classNames from "classnames";
import { ButtonSize, Color, TooltipProps } from "utils/types/util.types";
import { buttonHoverColorToHex, buttonSizeToStylesDictionary, colorEnumToHex, colorToButtonHoverColorDictionary } from "utils/types/dictionaries";
import { Tooltip } from "react-tooltip";

type Props = {
	link?: string
	onClick?: () => void
	style?: CSSProperties
	className?: string
	size?: ButtonSize
	outline?: boolean
	color: Color
	tooltip?: TooltipProps
}

function ButtonWrapper({
	link,
	onClick,
	children,
	style,
	className = "",
	size = ButtonSize.LARGE,
	outline,
	color,
	tooltip,
}: PropsWithChildren<Props>) {

	const onButtonClick = (e: MouseEvent<HTMLElement>) => {
		e.stopPropagation();
		if (!onClick) return;
		onClick();
	}

	const mouseOutElementStyles =
		outline ?
			{
				...style,
				backgroundColor: "transparent",
				color: colorEnumToHex[ color ],
				border: `1px solid ${ colorEnumToHex[ color ] }`,
			} : {
				...style,
				backgroundColor: colorEnumToHex[ color ],
			}

	const mouseOverElementStyles =
		outline ?
			{
				...style,
				backgroundColor: colorEnumToHex[ color ],
				color: "white",
				border: `1px solid ${ colorEnumToHex[ color ] }`,
			} : {
				...style,
				backgroundColor: buttonHoverColorToHex[ colorToButtonHoverColorDictionary[ color ] ]
			}

	const [ elementStyles, setElementStyle ] = useState<CSSProperties>(mouseOutElementStyles)

	const elementClassName = classNames(`
		text-stone-50
		${ buttonSizeToStylesDictionary[ size ].paddingX } 
		${ buttonSizeToStylesDictionary[ size ].paddingY }
		${ buttonSizeToStylesDictionary[ size ].fontSize }
		rounded-3xl
		font-[600]`,
		{ [ className ]: className }
	);

	return (
		<>
			{
				isNotNull(link)
					?
					<Link
						to={ link }
						onClick={ onButtonClick }
						style={ elementStyles }
						className={ elementClassName }
						onMouseOver={ () => setElementStyle(mouseOverElementStyles) }
						onMouseOut={ () => setElementStyle(mouseOutElementStyles) }
						data-tooltip-id={ tooltip?.id ?? undefined }
					>
						{ children }
					</Link>
					:
					<button
						onClick={ onButtonClick }
						style={ elementStyles }
						className={ elementClassName }
						onMouseOver={ () => setElementStyle(mouseOverElementStyles) }
						onMouseOut={ () => setElementStyle(mouseOutElementStyles) }
						data-tooltip-id={ tooltip?.id ?? undefined }
					>
						{ children }
					</button>
			}
			{
				isNotNull(tooltip) &&
				<Tooltip id={ tooltip.id }>
					{ tooltip.message }
				</Tooltip>
			}
		</>
	);
}

export default ButtonWrapper;